import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const DISMISS_KEY_PREFIX = "wmleye.upgrade_banner.dismissedAt.";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Slim, dismissable "Unlock full access" banner rendered above the parent
 * dashboard for any non-premium parent. Respects a 7-day localStorage TTL
 * on the dismiss so we don't nag daily, and stays hidden for parents whose
 * status is "premium" or "refunded". Live Stripe Checkout is the only buy
 * path; if the mode probe fails, the CTA disables itself instead of
 * falling back to the legacy sandbox endpoint (which returns 410 in live
 * mode). */
export function UpgradeBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [err, setErr] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null);   // null=loading, "live"|"sandbox"|"error"

  // Namespace by parent id so a dismissal on account A never suppresses the
  // banner for account B on the same browser (shared preview URLs).
  const dismissKey = user?.id
    ? `${DISMISS_KEY_PREFIX}${user.id}`
    : null;

  useEffect(() => {
    if (!dismissKey) return;
    try {
      const raw = window.localStorage.getItem(dismissKey);
      if (!raw) { setDismissed(false); return; }
      const ts = parseInt(raw, 10);
      if (Number.isFinite(ts) && Date.now() - ts < DISMISS_TTL_MS) {
        setDismissed(true);
      } else {
        window.localStorage.removeItem(dismissKey);
        setDismissed(false);
      }
    } catch (_e) { /* localStorage unavailable */ }
  }, [dismissKey]);

  const fetchMode = useCallback(() => {
    setPaymentMode(null);
    setErr(null);
    api.get("/purchases/mode")
      .then((r) => setPaymentMode(r.data.mode))
      .catch(() => setPaymentMode("error"));
  }, []);

  useEffect(() => { fetchMode(); }, [fetchMode]);

  const dismiss = useCallback(() => {
    if (!dismissKey) return;
    try { window.localStorage.setItem(dismissKey, String(Date.now())); } catch (_e) {}
    setDismissed(true);
  }, [dismissKey]);

  const buy = useCallback(async () => {
    if (paymentMode !== "live") return;
    setBusy(true); setRedirecting(true); setErr(null);
    try {
      const r = await api.post("/purchases/checkout", {});
      if (r.data.url) {
        window.location.href = r.data.url;
        return;
      }
      setErr("Could not start checkout.");
      setRedirecting(false);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Couldn't reach checkout. Try again.");
      setRedirecting(false);
    } finally {
      setBusy(false);
    }
  }, [paymentMode]);

  const status = user?.subscriptionStatus || "free";
  if (dismissed) return null;
  if (status === "premium" || status === "refunded") return null;

  const canBuy = paymentMode === "live" && !busy && !redirecting;
  let label;
  if (redirecting) label = "Redirecting…";
  else if (busy) label = "…";
  else if (paymentMode === null) label = "Loading…";
  else if (paymentMode !== "live") label = "Unavailable";
  else label = "Unlock full access";

  return (
    <div
      role="region"
      aria-label="Upgrade to full access"
      data-testid="upgrade-banner"
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 16, padding: "12px 20px", marginBottom: 20,
        background: "linear-gradient(90deg,#ffd85c 0%,#ffb03b 100%)",
        borderRadius: 14, boxShadow: "0 1px 0 rgba(0,0,0,0.06)",
        flexWrap: "wrap",
      }}
    >
      <div style={{ minWidth: 220 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#3a2a10", lineHeight: 1.2 }}>
          Unlock every world for $4.99
        </div>
        <div style={{ fontSize: 13, color: "#5c4a1f", marginTop: 2 }}>
          One-time payment. No ads. No subscriptions.
        </div>
        {paymentMode === "error" && !err && (
          <div data-testid="upgrade-banner-mode-error" style={{ fontSize: 12, color: "#8a0e18", marginTop: 4 }}>
            Purchase temporarily unavailable ·{" "}
            <button
              type="button"
              onClick={fetchMode}
              data-testid="upgrade-banner-mode-retry"
              style={{ background: "transparent", border: "none", color: "#8a0e18", textDecoration: "underline", cursor: "pointer", padding: 0, fontWeight: 700, fontSize: 12 }}
            >Retry</button>
          </div>
        )}
        {err && (
          <div data-testid="upgrade-banner-error" style={{ fontSize: 12, color: "#8a0e18", marginTop: 4 }}>
            {err}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button
          data-testid="upgrade-banner-cta"
          onClick={buy}
          disabled={!canBuy}
          aria-busy={busy || redirecting}
          style={{
            padding: "10px 18px", borderRadius: 999, border: "none",
            background: canBuy ? "#c96a1f" : "#a68764", color: "#fff", fontWeight: 700, fontSize: 14,
            cursor: canBuy ? "pointer" : "not-allowed",
          }}
        >
          {label}
        </button>
        <button
          data-testid="upgrade-banner-dismiss"
          aria-label="Dismiss upgrade banner"
          onClick={dismiss}
          style={{
            padding: "6px 10px", borderRadius: 999, border: "none",
            background: "transparent", color: "#3a2a10", fontSize: 20,
            lineHeight: 1, cursor: "pointer",
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
